import { Form, Progress } from 'antd';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { showNotification } from 'app/providers/NotificationsProvider';
import { InvoiceCreditNoteType } from 'entities/CreditNote';
import {
  calculateInvoicePercents,
  CreditInvoiceParams,
  Invoice,
  InvoiceItem,
  InvoiceItemType,
  useCreditInvoiceMutation,
} from 'entities/Invoice';
import { UserPermissions } from 'entities/User';
import React, { FC, memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { Checkbox } from 'shared/ui/Checkbox';
import { InputNumber } from 'shared/ui/InputNumber';
import { Modal } from 'shared/ui/Modal';
import { Select } from 'shared/ui/Select';
import { TextArea } from 'shared/ui/TextArea';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';

interface CreditInvoiceProps {
  invoice: Invoice;
  action: ReactElement;
}

interface CalculatedInvoiceItem extends InvoiceItem {
  status: {
    paidAmount: number;
    creditedAmount: number;
    unpaidAmount: number;
  };
}

export const CreditInvoice: FC<CreditInvoiceProps> = memo((props) => {
  const { invoice, action } = props;

  const [isOpenedModal, setOpenedModal] = useState(false);
  const [creditAll, setCreditAll] = useState(true);

  const isForbidden = useCheckPermission(UserPermissions.CREDIT_INVOICE);
  const { t } = useAppTranslation(['contracts', 'common']);
  const currencySymbol = useGetCurrencySymbol();
  const [form] = Form.useForm();

  const [creditInvoice, { isLoading }] = useCreditInvoiceMutation();
  const [invoiceItemsToCredit, setInvoiceItemsToCredit] = useState<Record<string, number>>({});

  const calculatedInvoiceItems = useMemo<CalculatedInvoiceItem[]>(() => {
    const paidByInvoiceItemId: Record<string, number> = {};

    (invoice.payments || []).forEach((payment) => {
      (payment.paymentItems || []).forEach((paymentItem) => {
        const invoiceItemId = paymentItem.invoiceItemId || paymentItem.invoiceItem?.invoiceItemId;

        if (!invoiceItemId) {
          return;
        }

        const cancelledBack = paymentItem.creditNoteItem?.creditedAmount || 0;
        const effectiveAmount = Math.max(0, roundNumber(paymentItem.amount - cancelledBack));

        paidByInvoiceItemId[invoiceItemId] = roundNumber((paidByInvoiceItemId[invoiceItemId] || 0) + effectiveAmount);
      });
    });

    const orders: Record<string, number> = {
      [InvoiceItemType.DEPOSIT]: 1,
      [InvoiceItemType.INSURANCE]: 2,
      [InvoiceItemType.RENTAL_ITEM]: 4,
    };

    // sort to calc deposit then insurance and then rent items
    const ordered = invoice.invoiceItems
      .map((invoiceItem) => {
        const order = orders[invoiceItem.invoiceItemType] || 3;

        return { ...invoiceItem, order };
      })
      .sort((a, b) => a.order - b.order);

    const calculatedData = ordered.map((invoiceItem) => {
      const creditedAmount = roundNumber(invoiceItem.creditNoteItems.reduce((acc, creditItem) => acc + creditItem.creditedAmount, 0));

      const amountToPay = roundNumber(invoiceItem.totalPrice - creditedAmount);

      const paidAmount = Math.min(paidByInvoiceItemId[invoiceItem.invoiceItemId] || 0, amountToPay);

      const unpaidAmount = roundNumber(amountToPay - paidAmount);

      return {
        ...invoiceItem,
        status: {
          paidAmount,
          creditedAmount,
          unpaidAmount,
        },
      };
    });

    return calculatedData;
  }, [invoice.invoiceItems, invoice.payments]);

  const openModal = useCallback((): void => {
    setOpenedModal(true);
  }, []);

  const closeModal = useCallback((): void => {
    setOpenedModal(false);
    setInvoiceItemsToCredit({});
    setCreditAll(true);
  }, []);

  useEffect(() => {
    if (isOpenedModal) {
      const defaultCreditValues = calculatedInvoiceItems.reduce((acc, invoiceItem) => {
        return { ...acc, [invoiceItem.invoiceItemId]: invoiceItem.status.unpaidAmount };
      }, {});

      if (creditAll) {
        setInvoiceItemsToCredit(defaultCreditValues);
      } else {
        setInvoiceItemsToCredit({});
      }
    }
  }, [calculatedInvoiceItems, isOpenedModal, creditAll]);

  useEffect(() => {
    if (invoice) {
      form.setFieldValue('creditNoteType', InvoiceCreditNoteType.DEBT_CREDIT);
    }
  }, [invoice, form]);

  const handleCreditInvoice = useCallback(
    async (data: CreditInvoiceParams): Promise<void> => {
      try {
        const creditNoteItems = Object.entries(invoiceItemsToCredit)
          .filter(([_, creditedAmount]) => creditedAmount)
          .map(([invoiceItemId, creditedAmount]) => {
            const invoiceItem = invoice.invoiceItems.find((item) => item.invoiceItemId === invoiceItemId);

            return {
              invoiceItemId,
              creditedAmount,
              creditNoteItemType: invoiceItem!.invoiceItemType,
              description: invoiceItem?.description,
            };
          });
        if (invoice.invoiceId && creditNoteItems.length) {
          await creditInvoice({ ...data, invoiceId: invoice.invoiceId, creditNoteItems }).unwrap();
          showNotification('info', t('Success', { ns: 'common' }), t('Invoice has been successfully credited'));
        }
      } catch (error: CustomAny) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when crediting invoice'));
      } finally {
        closeModal();
      }
    },
    [closeModal, creditInvoice, invoice, invoiceItemsToCredit, t],
  );

  const creditNoteTypeOptions = useMemo(
    () => [
      { label: t(InvoiceCreditNoteType.DEBT_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.DEBT_CREDIT },
      { label: t(InvoiceCreditNoteType.SERVICE_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.SERVICE_CREDIT },
      { label: t(InvoiceCreditNoteType.IMPAIRMENT_DEBT_CREDIT, { ns: 'common' }), value: InvoiceCreditNoteType.IMPAIRMENT_DEBT_CREDIT },
    ],
    [t],
  );

  const hasInvalidCreditAmounts = useMemo(
    () =>
      !Object.values(invoiceItemsToCredit).length ||
      calculatedInvoiceItems.some((invoiceItem) => invoiceItemsToCredit[invoiceItem.invoiceItemId] > invoiceItem.status.unpaidAmount),
    [calculatedInvoiceItems, invoiceItemsToCredit],
  );

  return (
    <>
      {React.cloneElement(action, { onClick: openModal })}

      <Modal className="max-w-[900px]" width={900} isOpen={isOpenedModal} onClose={closeModal}>
        <Form form={form} layout="vertical" onFinish={handleCreditInvoice} className="flex flex-col" disabled={isForbidden}>
          <div className="font-semibold text-xl mb-3">{t('Credit invoice')}</div>

          <div className="flex flex-wrap gap-10 mb-4">
            <div className="max-w-[350px] flex-1 mb-6">
              <div className="font-normal text-xl mb-5">{t('Base info')}</div>

              <div className="mb-4">
                <Checkbox
                  name="creditAll"
                  checked={creditAll}
                  onChange={(isChecked) => {
                    setCreditAll(isChecked);
                  }}
                >
                  {t('Credit all')}
                </Checkbox>
              </div>

              <Form.Item
                label={t('Credit note type')}
                name="creditNoteType"
                className="flex-1"
                rules={[
                  {
                    required: true,
                    message: t('Please select creditNoteType!'),
                  },
                ]}
              >
                <Select options={creditNoteTypeOptions} placeholder={t('Please select creditNoteType!')} bordered />
              </Form.Item>
              <Form.Item name="description">
                <TextArea placeholder={t('Add your description...')} rows={4} bordered />
              </Form.Item>
            </div>

            <div className="max-w-[500px] min-w-[300px] flex-1">
              <div className="font-normal text-xl mb-3">{t('Invoice items')}</div>

              {calculatedInvoiceItems.map((invoiceItem) => {
                const rows = [
                  {
                    label: t('Paid'),
                    value: `${invoiceItem.status.paidAmount} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('Credited'),
                    value: `${invoiceItem.status.creditedAmount} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('Debt'),
                    value: `${invoiceItem.status.unpaidAmount} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                ];

                const percents = calculateInvoicePercents(
                  invoiceItem.totalPrice,
                  invoiceItem.status.paidAmount,
                  invoiceItem.status.creditedAmount,
                );

                const unpaidPercent = roundNumber(100 - percents.paid - percents.credited);

                const splitedDescription = splitDocumentDescription(invoiceItem.description);

                const isRentalItem = invoiceItem.invoiceItemType === InvoiceItemType.RENTAL_ITEM;
                const invoieItemType = isRentalItem
                  ? invoiceItem.contractItem?.rentalItem?.rentalItemType || invoiceItem.invoiceItemType
                  : invoiceItem.invoiceItemType;

                return (
                  <div className="border border-secondary rounded-lg my-2 p-4" key={invoiceItem.invoiceItemId}>
                    <div className="flex flex-col mb-2 gap-2">
                      <div className="font-semibold text-xl flex">
                        {t(invoieItemType)}:
                        <span className="font-normal text-xl px-1">
                          {invoiceItem.totalPrice} {currencySymbol}
                        </span>
                      </div>
                      <div>{`${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`}</div>
                    </div>
                    <div className="mb-3">
                      <Progress
                        size={['100%', 10]}
                        percent={percents.paid + unpaidPercent}
                        success={{ percent: percents.paid }}
                        strokeColor="var(--primaryLight)"
                        trailColor="var(--warn)"
                        showInfo={false}
                      />
                    </div>
                    <div className="flex flex-row items-end">
                      <div className="w-[50%]">
                        <BulletsTable theme="clear" rows={rows} />
                      </div>
                      <div className="w-[50%]">
                        <InputNumber
                          onChange={(value) => {
                            setInvoiceItemsToCredit((prev) => ({ ...prev, [invoiceItem.invoiceItemId]: value || 0 }));
                          }}
                          value={invoiceItemsToCredit[invoiceItem.invoiceItemId]}
                          placeholder={t('Credit amount')}
                          status={invoiceItemsToCredit[invoiceItem.invoiceItemId] > invoiceItem.status.unpaidAmount ? 'error' : undefined}
                          isDisabled={creditAll}
                          bordered
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button theme="secondary" onClick={closeModal}>
              {t('Cancel', { ns: 'common' })}
            </Button>

            <Button type="submit" isLoading={isLoading} isDisabled={isForbidden || hasInvalidCreditAmounts}>
              {t('Save', { ns: 'common' })}
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
});
